import AreYouSurToContinueModal from "@/components/molecules/modals/AreYouSurToContinueModal";
import StoreMontageMeetingStatusModal from "@/components/molecules/modals/montage/StoreMontageMeetingStatusModal";
import SoftBadge from "@/components/atoms/SoftBadge";
import AllMontageMeetingStatusesHeader from "@/components/molecules/table-headers/AllMontageMeetingStatusesHeader";
import Table from "@/components/molecules/table/Table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTableState } from "@/hooks/useTableState";
import MontageMeetingStatus from "@/interfaces/MontageMeetingStatus";
import { IDndChange } from "@/interfaces/Table";
import {
  useDeleteMontageMeetingStatusMutation,
  useGetMontageMeetingStatusesQuery,
  useUpdateMontageMeetingStatusMutation,
} from "@/services/montageMeetingStatuses";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { ElementRef, FC, useRef } from "react";
import { Button } from "react-bootstrap";
import { ToastContentProps, toast } from "react-toastify";

const MontageMeetingStatusesView: FC = () => {
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const EditModalRef =
    useRef<ElementRef<typeof StoreMontageMeetingStatusModal>>(null);
  const [deleteStatusAsync] = useDeleteMontageMeetingStatusMutation();
  const [updateStatusAsync] = useUpdateMontageMeetingStatusMutation();

  const [state, setState] = useTableState<Partial<TableState>>(
    "montageMeetingStatuses",
    {
      pagination: { pageIndex: 0, pageSize: 15 },
      globalFilter: "",
    },
  );

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);

  const { data } = useGetMontageMeetingStatusesQuery({
    ...state.pagination,
    query: debouncedQuery,
  });

  const changeOrderAsync = async ({ fromId, toId }: IDndChange) => {
    await toast.promise(updateStatusAsync([fromId, { toId }]).unwrap(), {
      pending: "Changement de position en cours...",
      success: "Changement de position confirmé 🤩",
      error: "Une erreur est survenue",
    });
  };

  const onDelete = async (id: string) => {
    toast.promise(deleteStatusAsync(id).unwrap(), {
      pending: "Suppression du statut en cours ...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return "Le statut a bien été supprimé 🤩";
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          // Le dernier statut est protégé côté API : la règle « premier de la
          // liste par défaut » n'aurait plus rien à désigner.
          const message = (data?.data as { message?: string } | undefined)
            ?.message;
          return message ?? "Une erreur est survenue";
        },
      },
    });
  };

  const columns = useRef<ColumnDef<MontageMeetingStatus>[]>([
    {
      header: "Ordre",
      enableSorting: false,
      footer: (props) => props.column.id,
      id: "order",
      cell: (info) => info.getValue(),
    },
    {
      header: "Actions",
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: (status) => (
        <div>
          <Button
            variant="info"
            size="sm"
            onClick={() => EditModalRef.current?.showOrHiddenModal(status)}
          >
            <FontAwesomeIcon icon={faEdit} />
          </Button>
          <Button
            variant="danger"
            size="sm"
            className="ms-2"
            onClick={() => DeleteModalRef.current?.showOrHiddenModal(status.id)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </div>
      ),
      id: "actions",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nom",
      footer: (props) => props.column.id,
      accessorFn: ({ name, backgroundColor, textColor }) => (
        <SoftBadge bg={backgroundColor} textColor={textColor}>
          {name}
        </SoftBadge>
      ),
      id: "name",
      cell: (info) => info.getValue(),
    },
    {
      header: "Crée le",
      footer: (props) => props.column.id,
      accessorFn: (row) =>
        DateTime.fromISO(row.createdAt).toFormat("dd-MM-yyyy"),
      id: "createdAt",
      cell: (info) => info.getValue(),
    },
  ]).current;

  return (
    <div className="formalys-page formalys-list-page">
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment supprimer ce statut de rendez-vous montage ? Les rendez-vous concernés basculeront sur le premier statut restant."
      />
      <StoreMontageMeetingStatusModal
        title="Modification d'un statut de rendez-vous montage"
        ref={EditModalRef}
      />
      <Table
        resource="montage-meeting-statuses"
        tableName="montageMeetingStatuses"
        HeaderComponent={AllMontageMeetingStatusesHeader}
        columns={columns}
        onDndChange={changeOrderAsync}
        state={state}
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
      />
    </div>
  );
};

export default MontageMeetingStatusesView;
